import { isEmpty } from "lodash-es";
import { useMemo } from "react";

import { INPUT_LIST, LOOP, OUTPUT_LIST } from "src/constants/InternalDataKeys";
import {
  AvailableData,
  RowStatusType,
  TestResultPopoverProps,
} from "src/dataTable/DetailedView/tabs/types";
import { errorMessage } from "src/utils/stringUtils";

export const useIterationsData = ({
  error: historyError,
  rowData,
  data,
  selectedNodeId,
}: Omit<TestResultPopoverProps, "open" | "onClose" | "nodeID">) => {
  const availableData: AvailableData = useMemo(() => {
    // Historical data
    if (!isEmpty(data)) {
      if (Array.isArray(data[INPUT_LIST]) && Array.isArray(data[OUTPUT_LIST])) {
        const [inputList, outputList] = [data[INPUT_LIST], data[OUTPUT_LIST]];
        return {
          [INPUT_LIST]: inputList,
          [OUTPUT_LIST]: outputList,
          item: historyError ? inputList?.[inputList.length - 1] : undefined,
        };
      }

      const pendingLoopData = data[LOOP];

      if (!Array.isArray(pendingLoopData)) {
        return {
          [INPUT_LIST]: pendingLoopData?.target_list ?? [],
          [OUTPUT_LIST]: pendingLoopData?.output_list ?? [],
          item: pendingLoopData?.item ?? {},
        };
      }
    }

    // test run data here in any case
    if (rowData?.type === "failure" || rowData?.type === "ignored") {
      const provenanceData = rowData?.provenance?.[selectedNodeId] ?? {};
      const [inputList, outputList] = [
        provenanceData[INPUT_LIST],
        provenanceData[OUTPUT_LIST],
      ];
      return provenanceData
        ? {
            [INPUT_LIST]: inputList,
            [OUTPUT_LIST]: outputList,
            item:
              rowData.type === "failure"
                ? inputList?.[inputList?.length - 1]
                : undefined,
          }
        : {};
    } else {
      return { ...rowData?.data };
    }
  }, [rowData, data, selectedNodeId, historyError]);

  const status: RowStatusType = (() => {
    if (Boolean(historyError) || (rowData && rowData.type === "failure")) {
      return "failure";
    }

    if (rowData?.type === "ignored") {
      return "ignored";
    }

    if (availableData.item) {
      return "pending";
    }

    return "success";
  })();

  return {
    availableData,
    status,
    error: historyError
      ? errorMessage(historyError.msg)
      : rowData?.type === "failure" && rowData?.errorMessage,
  };
};
